<template>
	<div>
		<div class="nk-app-root">
			<div class="nk-main">
				<div class="nk-wrap">
					<Header v-show="!loading_page" />
					<div class="nk-content">
						<div class="container wide-xl">
							<div class="nk-content-inner">
								<Navigation v-show="!loading_page" />
								<div class="nk-content-body" v-show="!loading_page">
									<div class="nk-content-wrap">
										<div class="nk-block nk-block-lg">
											<div class="nk-block-head wide-md nk-block-head-lg">
												<div class="nk-block-head-sub">
													<a
														class="back-to"
														href="#"
														@click.prevent="$router.push('/dm/anlagen')"
													>
														<em class="icon ni ni-arrow-left"></em>
														<span>{{ $t('dm_common_btn_back') }}</span>
													</a>
												</div>
												<div class="nk-block-head-content">
													<div class="nk-block-head-sub">
														<span>8.2 Inspect</span>
														<span
															v-if="
																anlage.status_8p2 &&
																	anlage.status_8p2.bestaetigt
															"
															class="badge dm-badge-confirmed ml-2"
															>{{ $t('dm_common_badge_confirmed') }}</span
														>
													</div>
													<h2 class="nk-block-title fw-normal">
														{{
															isNew
																? $t('dm_anlage_new_title')
																: anlage.seriennummer
														}}
													</h2>
												</div>
											</div>

											<div class="alert alert-danger" v-if="errorMessage">
												{{ errorMessage }}
											</div>

											<!-- Grunddaten -->
											<div class="card card-bordered">
												<div class="card-inner">
													<h5 class="card-title">
														{{ $t('dm_anlage_section_basic') }}
													</h5>
													<div class="row gy-4">
														<div class="col-sm-3">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_common_col_serial_number')
																}}</label>
																<input
																	type="text"
																	class="form-control"
																	v-model="anlage.seriennummer"
																	:disabled="!canDmEdit"
																/>
															</div>
														</div>
														<div class="col-sm-3">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_common_col_system_type')
																}}</label>
																<select
																	class="form-control"
																	v-model="anlage.typ"
																	:disabled="!canDmEdit"
																>
																	<option value="">{{
																		$t('dm_common_please_select')
																	}}</option>
																	<option value="WEA">WEA</option>
																	<option value="PV">PV</option>
																	<option value="BAT">BAT</option>
																</select>
															</div>
														</div>
														<div class="col-sm-3">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_anlage_label_manufacturer_abbr')
																}}</label>
																<input
																	type="text"
																	class="form-control"
																	v-model="anlage.hersteller_kuerzel"
																	:disabled="!canDmEdit"
																/>
															</div>
														</div>
														<div class="col-sm-3">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_common_col_park')
																}}</label>
																<select
																	class="form-control"
																	v-model="anlage.standort.park_id"
																	:disabled="!canDmEdit"
																>
																	<option value="">{{
																		$t('dm_anlage_no_park')
																	}}</option>
																	<option
																		v-for="p in parks"
																		:key="p._id"
																		:value="p._id"
																		>{{ p.name }}</option
																	>
																</select>
															</div>
														</div>
													</div>
													<div
														class="row gy-4 mt-2"
														v-if="
															anlage.contractors &&
																anlage.contractors.length > 0
														"
													>
														<div class="col-12">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_common_col_contractors')
																}}</label>
																<div class="d-flex flex-wrap" style="gap: 6px;">
																	<span
																		v-for="c in anlage.contractors"
																		:key="c"
																		class="badge badge-dim badge-outline-secondary"
																		>{{ c }}</span
																	>
																</div>
															</div>
														</div>
													</div>
												</div>
											</div>

											<!-- Anlagendaten -->
											<div class="card card-bordered mt-3">
												<div class="card-inner">
													<h5 class="card-title">
														{{ $t('dm_anlage_section_system_data') }}
													</h5>
													<div class="row gy-4">
														<div class="col-sm-6">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_common_col_manufacturer')
																}}</label>
																<input
																	type="text"
																	class="form-control"
																	v-model="anlage.anlagendaten.hersteller"
																	:disabled="!canDmEdit"
																/>
															</div>
														</div>
														<div class="col-sm-6">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_common_col_type_name')
																}}</label>
																<input
																	type="text"
																	class="form-control"
																	v-model="anlage.anlagendaten.typ_bezeichnung"
																	:disabled="!canDmEdit"
																/>
															</div>
														</div>
													</div>
													<div class="row gy-4 mt-2">
														<div class="col-sm-6">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_common_col_power_kw')
																}}</label>
																<input
																	type="number"
																	class="form-control"
																	v-model.number="
																		anlage.anlagendaten.leistung_kw
																	"
																	:disabled="!canDmEdit"
																/>
															</div>
														</div>
														<div class="col-sm-6">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_anlage_label_commissioning_date')
																}}</label>
																<input
																	type="date"
																	class="form-control"
																	v-model="anlage.anlagendaten.ib_datum"
																	:disabled="!canDmEdit"
																/>
															</div>
														</div>
													</div>
													<hr class="preview-hr" />
													<h6 class="overline-title overline-title-alt mb-3">
														{{ $t('dm_anlage_section_additional_info') }}
													</h6>
													<div class="row gy-4">
														<div class="col-sm-6">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_anlage_label_site_access')
																}}</label>
																<input
																	type="text"
																	class="form-control"
																	v-model="anlage.anlagendaten.anlagenzugang"
																	:disabled="!canDmEdit"
																/>
															</div>
														</div>
														<div class="col-sm-6">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_anlage_label_special_features')
																}}</label>
																<input
																	type="text"
																	class="form-control"
																	v-model="anlage.anlagendaten.besonderheiten"
																	:disabled="!canDmEdit"
																/>
															</div>
														</div>
													</div>
												</div>
											</div>

											<!-- Vertragsinfo -->
											<div class="card card-bordered mt-3">
												<div class="card-inner">
													<h5 class="card-title">
														{{ $t('dm_anlage_section_contract_info') }}
													</h5>
													<div class="row gy-4">
														<div class="col-sm-4">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_anlage_label_epc_status')
																}}</label>
																<select
																	class="form-control"
																	v-model="anlage.vertragsinfo.epk.status"
																	:disabled="!canDmEdit"
																>
																	<option value="">{{
																		$t('dm_common_no_specification')
																	}}</option>
																	<option value="ja">{{
																		$t('dm_common_yes')
																	}}</option>
																	<option value="nein">{{
																		$t('dm_common_no')
																	}}</option>
																	<option value="wartungsvertrag_hersteller">{{
																		$t('dm_anlage_epk_maintenance_contract')
																	}}</option>
																	<option value="abgelaufen">{{
																		$t('dm_anlage_epk_expired')
																	}}</option>
																</select>
															</div>
														</div>
														<div class="col-sm-4">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_anlage_label_epc_end_date')
																}}</label>
																<input
																	type="date"
																	class="form-control"
																	v-model="
																		anlage.vertragsinfo.epk.epk_ende_datum
																	"
																	:disabled="!canDmEdit"
																/>
															</div>
														</div>
													</div>
												</div>
											</div>

											<!-- Projekt -->
											<div class="card card-bordered mt-3">
												<div class="card-inner">
													<h5 class="card-title">
														{{ $t('dm_common_col_project') }}
													</h5>
													<div class="row gy-4">
														<div class="col-sm-4">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_anlage_label_project_old')
																}}</label>
																<input
																	type="text"
																	class="form-control"
																	v-model="anlage.projekt.projekt_alt"
																	:disabled="!canDmEdit"
																/>
															</div>
														</div>
														<div class="col-sm-4">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_anlage_label_project_new')
																}}</label>
																<input
																	type="text"
																	class="form-control"
																	v-model="anlage.projekt.projekt_neu"
																	:disabled="!canDmEdit"
																/>
															</div>
														</div>
														<div class="col-sm-4">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_anlage_label_to_clarify')
																}}</label>
																<input
																	type="text"
																	class="form-control"
																	v-model="anlage.projekt.zu_klaeren"
																	:disabled="!canDmEdit"
																/>
															</div>
														</div>
													</div>
												</div>
											</div>

											<!-- Standort -->
											<div class="card card-bordered mt-3">
												<div class="card-inner">
													<h5 class="card-title">
														{{ $t('dm_common_col_location') }}
													</h5>
													<h6 class="overline-title overline-title-alt mb-3">
														{{ $t('dm_common_col_designation') }}
													</h6>
													<div class="row gy-4">
														<div class="col-sm-6">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_anlage_label_site_name')
																}}</label>
																<input
																	type="text"
																	class="form-control"
																	v-model="anlage.standort.name"
																	:disabled="!canDmEdit"
																/>
															</div>
														</div>
														<div class="col-sm-6">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_anlage_label_site_no_manufacturer')
																}}</label>
																<input
																	type="text"
																	class="form-control"
																	v-model="
																		anlage.standort.standortnr_hersteller
																	"
																	:disabled="!canDmEdit"
																/>
															</div>
														</div>
													</div>
													<div class="row gy-4 mt-2">
														<div class="col-sm-6">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_anlage_label_site_no_building_permit')
																}}</label>
																<input
																	type="text"
																	class="form-control"
																	v-model="
																		anlage.standort.standortnr_baugenehmigung
																	"
																	:disabled="!canDmEdit"
																/>
															</div>
														</div>
														<div class="col-sm-6">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_anlage_label_site_no_planner')
																}}</label>
																<input
																	type="text"
																	class="form-control"
																	v-model="
																		anlage.standort.standortnr_projektierer
																	"
																	:disabled="!canDmEdit"
																/>
															</div>
														</div>
													</div>
													<hr class="preview-hr" />
													<h6 class="overline-title overline-title-alt mb-3">
														{{ $t('dm_common_col_address') }}
													</h6>
													<div class="row gy-4">
														<div class="col-sm-6">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_common_col_street')
																}}</label>
																<input
																	type="text"
																	class="form-control"
																	v-model="anlage.standort.adresse.strasse"
																	:disabled="!canDmEdit"
																/>
															</div>
														</div>
														<div class="col-sm-3">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_common_col_zip')
																}}</label>
																<input
																	type="text"
																	class="form-control"
																	v-model="anlage.standort.adresse.plz"
																	:disabled="!canDmEdit"
																/>
															</div>
														</div>
														<div class="col-sm-3">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_common_col_city')
																}}</label>
																<input
																	type="text"
																	class="form-control"
																	v-model="anlage.standort.adresse.ort"
																	:disabled="!canDmEdit"
																/>
															</div>
														</div>
													</div>
													<div class="row gy-4 mt-2">
														<div class="col-sm-6">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_common_col_state')
																}}</label>
																<input
																	type="text"
																	class="form-control"
																	v-model="anlage.standort.adresse.bundesland"
																	:disabled="!canDmEdit"
																/>
															</div>
														</div>
														<div class="col-sm-6">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_common_col_country')
																}}</label>
																<input
																	type="text"
																	class="form-control"
																	v-model="anlage.standort.adresse.land"
																	:disabled="!canDmEdit"
																/>
															</div>
														</div>
													</div>
													<hr class="preview-hr" />
													<h6 class="overline-title overline-title-alt mb-3">
														{{ $t('dm_anlage_section_coordinates') }}
													</h6>
													<div class="row gy-4">
														<div class="col-sm-6">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_common_col_longitude')
																}}</label>
																<input
																	type="number"
																	step="0.000001"
																	class="form-control"
																	v-model.number="longitude"
																	:disabled="!canDmEdit"
																/>
															</div>
														</div>
														<div class="col-sm-6">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_common_col_latitude')
																}}</label>
																<input
																	type="number"
																	step="0.000001"
																	class="form-control"
																	v-model.number="latitude"
																	:disabled="!canDmEdit"
																/>
															</div>
														</div>
													</div>
												</div>
											</div>

											<!-- Anschriften -->
											<div class="card card-bordered mt-3">
												<div class="card-inner">
													<h5 class="card-title">
														{{ $t('dm_anlage_section_contacts') }}
													</h5>
													<h6 class="overline-title overline-title-alt mb-3">
														{{ $t('dm_anlage_section_assignments') }}
													</h6>
													<div class="row gy-4">
														<div class="col-sm-6">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_anlage_label_operator')
																}}</label>
																<select
																	class="form-control"
																	v-model="anlage.anschriften.betreiber_id"
																	:disabled="!canDmEdit"
																>
																	<option value="">{{
																		$t('dm_common_no_specification')
																	}}</option>
																	<option
																		v-for="a in adressen"
																		:key="a._id"
																		:value="a._id"
																		>{{ a.firma || a.ansprechpartner }}</option
																	>
																</select>
															</div>
														</div>
														<div class="col-sm-6">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_anlage_label_contractor')
																}}</label>
																<select
																	class="form-control"
																	v-model="anlage.anschriften.auftragnehmer_id"
																	:disabled="!canDmEdit"
																>
																	<option value="">{{
																		$t('dm_common_no_specification')
																	}}</option>
																	<option
																		v-for="a in adressen"
																		:key="a._id"
																		:value="a._id"
																		>{{ a.firma || a.ansprechpartner }}</option
																	>
																</select>
															</div>
														</div>
													</div>
													<div class="row gy-4 mt-2">
														<div class="col-sm-6">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_anlage_label_operations_manager')
																}}</label>
																<select
																	class="form-control"
																	v-model="
																		anlage.anschriften.betriebsfuehrer_id
																	"
																	:disabled="!canDmEdit"
																>
																	<option value="">{{
																		$t('dm_common_no_specification')
																	}}</option>
																	<option
																		v-for="a in adressen"
																		:key="a._id"
																		:value="a._id"
																		>{{ a.firma || a.ansprechpartner }}</option
																	>
																</select>
															</div>
														</div>
														<div class="col-sm-6">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_anlage_label_invoice_recipient')
																}}</label>
																<select
																	class="form-control"
																	v-model="
																		anlage.anschriften.rechnungsempfaenger_id
																	"
																	:disabled="!canDmEdit"
																>
																	<option value="">{{
																		$t('dm_common_no_specification')
																	}}</option>
																	<option
																		v-for="a in adressen"
																		:key="a._id"
																		:value="a._id"
																		>{{ a.firma || a.ansprechpartner }}</option
																	>
																</select>
															</div>
														</div>
													</div>
													<div class="row gy-4 mt-2">
														<div class="col-sm-6">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_anlage_label_approval_authority')
																}}</label>
																<select
																	class="form-control"
																	v-model="
																		anlage.anschriften.genehmigungsbehoerde_id
																	"
																	:disabled="!canDmEdit"
																>
																	<option value="">{{
																		$t('dm_common_no_specification')
																	}}</option>
																	<option
																		v-for="a in adressen"
																		:key="a._id"
																		:value="a._id"
																		>{{ a.firma || a.ansprechpartner }}</option
																	>
																</select>
															</div>
														</div>
													</div>
													<hr class="preview-hr" />
													<h6 class="overline-title overline-title-alt mb-3">
														{{ $t('dm_anlage_section_site_manager') }}
													</h6>
													<div class="row gy-4">
														<div class="col-sm-4">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_common_col_name')
																}}</label>
																<input
																	type="text"
																	class="form-control"
																	v-model="
																		anlage.anschriften.anlagen_betreuer_vor_ort
																			.name
																	"
																	:disabled="!canDmEdit"
																/>
															</div>
														</div>
														<div class="col-sm-4">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_common_col_phone')
																}}</label>
																<input
																	type="text"
																	class="form-control"
																	v-model="
																		anlage.anschriften.anlagen_betreuer_vor_ort
																			.telefon
																	"
																	:disabled="!canDmEdit"
																/>
															</div>
														</div>
														<div class="col-sm-4">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_common_col_email')
																}}</label>
																<input
																	type="email"
																	class="form-control"
																	v-model="
																		anlage.anschriften.anlagen_betreuer_vor_ort
																			.email
																	"
																	:disabled="!canDmEdit"
																/>
															</div>
														</div>
													</div>
												</div>
											</div>

											<!-- WEA-spezifische Daten -->
											<div
												class="card card-bordered mt-3"
												v-if="anlage.typ === 'WEA'"
											>
												<div class="card-inner">
													<h5 class="card-title">
														{{ $t('dm_anlage_section_wea_data') }}
													</h5>
													<div class="row gy-4">
														<div class="col-sm-4">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_anlage_label_rotor_diameter_m')
																}}</label>
																<input
																	type="number"
																	class="form-control"
																	v-model.number="
																		anlage.wea_daten.rotordurchmesser_m
																	"
																	:disabled="!canDmEdit"
																/>
															</div>
														</div>
														<div class="col-sm-4">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_anlage_label_hub_height_m')
																}}</label>
																<input
																	type="number"
																	class="form-control"
																	v-model.number="anlage.wea_daten.nabenhoehe_m"
																	:disabled="!canDmEdit"
																/>
															</div>
														</div>
														<div class="col-sm-4">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_common_col_technology')
																}}</label>
																<input
																	type="text"
																	class="form-control"
																	v-model="anlage.wea_daten.technologie"
																	:disabled="!canDmEdit"
																/>
															</div>
														</div>
													</div>
												</div>
											</div>

											<!-- PV-spezifische Daten -->
											<div
												class="card card-bordered mt-3"
												v-if="anlage.typ === 'PV'"
											>
												<div class="card-inner">
													<h5 class="card-title">
														{{ $t('dm_anlage_section_pv_data') }}
													</h5>
													<h6 class="overline-title overline-title-alt mb-3">
														{{ $t('dm_anlage_section_modules') }}
													</h6>
													<div class="row gy-4">
														<div class="col-sm-6">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_anlage_label_power_kwp')
																}}</label>
																<input
																	type="number"
																	class="form-control"
																	v-model.number="anlage.pv_daten.leistung_kwp"
																	:disabled="!canDmEdit"
																/>
															</div>
														</div>
														<div class="col-sm-6">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_anlage_label_module_count')
																}}</label>
																<input
																	type="number"
																	class="form-control"
																	v-model.number="anlage.pv_daten.modulanzahl"
																	:disabled="!canDmEdit"
																/>
															</div>
														</div>
													</div>
													<div class="row gy-4 mt-2">
														<div class="col-sm-6">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_anlage_label_module_type')
																}}</label>
																<input
																	type="text"
																	class="form-control"
																	v-model="anlage.pv_daten.modultyp"
																	:disabled="!canDmEdit"
																/>
															</div>
														</div>
														<div class="col-sm-6">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_anlage_label_module_manufacturer')
																}}</label>
																<input
																	type="text"
																	class="form-control"
																	v-model="anlage.pv_daten.modulhersteller"
																	:disabled="!canDmEdit"
																/>
															</div>
														</div>
													</div>
													<hr class="preview-hr" />
													<h6 class="overline-title overline-title-alt mb-3">
														{{ $t('dm_anlage_section_installation') }}
													</h6>
													<div class="row gy-4">
														<div class="col-sm-4">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_anlage_label_inverter_type')
																}}</label>
																<input
																	type="text"
																	class="form-control"
																	v-model="anlage.pv_daten.wechselrichter_typ"
																	:disabled="!canDmEdit"
																/>
															</div>
														</div>
														<div class="col-sm-4">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_anlage_label_tilt_angle_degrees')
																}}</label>
																<input
																	type="number"
																	class="form-control"
																	v-model.number="
																		anlage.pv_daten.aufstellungswinkel_grad
																	"
																	:disabled="!canDmEdit"
																/>
															</div>
														</div>
														<div class="col-sm-4">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_anlage_label_azimuth_degrees')
																}}</label>
																<input
																	type="number"
																	class="form-control"
																	v-model.number="
																		anlage.pv_daten.ausrichtung_grad
																	"
																	:disabled="!canDmEdit"
																/>
															</div>
														</div>
													</div>
													<div class="row gy-4 mt-2">
														<div class="col-sm-4">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_anlage_label_area_m2')
																}}</label>
																<input
																	type="number"
																	class="form-control"
																	v-model.number="anlage.pv_daten.flaeche_m2"
																	:disabled="!canDmEdit"
																/>
															</div>
														</div>
														<div class="col-sm-4">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_anlage_label_tracking')
																}}</label>
																<select
																	class="form-control"
																	v-model="anlage.pv_daten.tracking"
																	:disabled="!canDmEdit"
																>
																	<option :value="true">{{
																		$t('dm_common_yes')
																	}}</option>
																	<option :value="false">{{
																		$t('dm_common_no')
																	}}</option>
																</select>
															</div>
														</div>
													</div>
												</div>
											</div>

											<!-- BAT-spezifische Daten -->
											<div
												class="card card-bordered mt-3"
												v-if="anlage.typ === 'BAT'"
											>
												<div class="card-inner">
													<h5 class="card-title">
														{{ $t('dm_anlage_section_battery_data') }}
													</h5>
													<h6 class="overline-title overline-title-alt mb-3">
														{{ $t('dm_anlage_section_power') }}
													</h6>
													<div class="row gy-4">
														<div class="col-sm-4">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_anlage_label_capacity_kwh')
																}}</label>
																<input
																	type="number"
																	class="form-control"
																	v-model.number="
																		anlage.bat_daten.kapazitaet_kwh
																	"
																	:disabled="!canDmEdit"
																/>
															</div>
														</div>
														<div class="col-sm-4">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_anlage_label_discharge_power_kw')
																}}</label>
																<input
																	type="number"
																	class="form-control"
																	v-model.number="
																		anlage.bat_daten.leistung_kw_entladung
																	"
																	:disabled="!canDmEdit"
																/>
															</div>
														</div>
														<div class="col-sm-4">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_anlage_label_charge_power_kw')
																}}</label>
																<input
																	type="number"
																	class="form-control"
																	v-model.number="
																		anlage.bat_daten.leistung_kw_ladung
																	"
																	:disabled="!canDmEdit"
																/>
															</div>
														</div>
													</div>
													<hr class="preview-hr" />
													<h6 class="overline-title overline-title-alt mb-3">
														{{ $t('dm_anlage_section_specification') }}
													</h6>
													<div class="row gy-4">
														<div class="col-sm-4">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_common_col_technology')
																}}</label>
																<input
																	type="text"
																	class="form-control"
																	v-model="anlage.bat_daten.technologie"
																	:disabled="!canDmEdit"
																/>
															</div>
														</div>
														<div class="col-sm-4">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_anlage_label_number_of_modules')
																}}</label>
																<input
																	type="number"
																	class="form-control"
																	v-model.number="
																		anlage.bat_daten.anzahl_module
																	"
																	:disabled="!canDmEdit"
																/>
															</div>
														</div>
														<div class="col-sm-4">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_anlage_label_guaranteed_cycles')
																}}</label>
																<input
																	type="number"
																	class="form-control"
																	v-model.number="
																		anlage.bat_daten.zyklen_garantiert
																	"
																	:disabled="!canDmEdit"
																/>
															</div>
														</div>
													</div>
													<div class="row gy-4 mt-2">
														<div class="col-sm-4">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_anlage_label_operating_temp_min_c')
																}}</label>
																<input
																	type="number"
																	class="form-control"
																	v-model.number="
																		anlage.bat_daten.betriebstemperatur_min_c
																	"
																	:disabled="!canDmEdit"
																/>
															</div>
														</div>
														<div class="col-sm-4">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_anlage_label_operating_temp_max_c')
																}}</label>
																<input
																	type="number"
																	class="form-control"
																	v-model.number="
																		anlage.bat_daten.betriebstemperatur_max_c
																	"
																	:disabled="!canDmEdit"
																/>
															</div>
														</div>
													</div>
												</div>
											</div>

											<!-- Betriebseinschränkungen -->
											<div class="card card-bordered mt-3">
												<div class="card-inner">
													<h5
														class="card-title d-flex justify-content-between align-items-center"
													>
														{{ $t('dm_anlage_section_operating_restrictions') }}
														<button
															v-if="canDmEdit"
															class="btn btn-sm btn-outline-primary"
															@click="addBetriebseinschraenkung"
														>
															<em class="icon ni ni-plus"></em>
														</button>
													</h5>
													<div
														v-if="
															!anlage.betriebseinschraenkungen ||
																anlage.betriebseinschraenkungen.length === 0
														"
														class="text-muted"
													>
														{{ $t('dm_anlage_no_operating_restrictions') }}
													</div>
													<div
														v-for="(be, idx) in anlage.betriebseinschraenkungen"
														:key="idx"
														class="row gy-4 mb-2"
													>
														<div class="col-sm-3">
															<div class="form-group">
																<label class="form-label" v-if="idx === 0">{{
																	$t('dm_common_col_type')
																}}</label>
																<input
																	type="text"
																	class="form-control"
																	v-model="be.typ"
																	:disabled="!canDmEdit"
																/>
															</div>
														</div>
														<div class="col-sm-8">
															<div class="form-group">
																<label class="form-label" v-if="idx === 0">{{
																	$t('dm_common_col_description')
																}}</label>
																<input
																	type="text"
																	class="form-control"
																	v-model="be.beschreibung"
																	:disabled="!canDmEdit"
																/>
															</div>
														</div>
														<div class="col-sm-1 d-flex align-items-end">
															<button
																v-if="canDmEdit"
																class="btn btn-sm btn-outline-danger"
																@click="
																	anlage.betriebseinschraenkungen.splice(idx, 1)
																"
															>
																<em class="icon ni ni-trash"></em>
															</button>
														</div>
													</div>
												</div>
											</div>

											<!-- Prüfungsfristen -->
											<div class="card card-bordered mt-3" v-if="!isNew">
												<div class="card-inner">
													<h5
														class="card-title d-flex justify-content-between align-items-center"
													>
														{{ $t('dm_anlage_section_inspection_dates') }}
														<button
															v-if="canDmEdit"
															class="btn btn-sm btn-outline-primary"
															@click="openPruefungModal(null)"
														>
															<em class="icon ni ni-plus"></em>
															{{ $t('dm_anlage_btn_add_inspection') }}
														</button>
													</h5>
													<div
														v-if="!pruefungen || pruefungen.length === 0"
														class="text-muted"
													>
														{{ $t('dm_anlage_no_inspection_dates') }}
													</div>
													<div
														class="table-responsive"
														v-if="pruefungen && pruefungen.length > 0"
													>
														<table class="table table-striped table-sm">
															<thead>
																<tr>
																	<th>{{ $t('dm_common_col_status') }}</th>
																	<th>{{ $t('dm_common_col_type') }}</th>
																	<th>{{ $t('dm_common_col_designation') }}</th>
																	<th>{{ $t('dm_common_col_due_date') }}</th>
																	<th>
																		{{ $t('dm_anlage_col_planned_date') }}
																	</th>
																	<th>{{ $t('dm_anlage_col_last_date') }}</th>
																	<th v-if="canDmEdit"></th>
																</tr>
															</thead>
															<tbody>
																<tr v-for="p in pruefungen" :key="p._id">
																	<td>
																		<span
																			class="badge"
																			:style="{
																				backgroundColor: getAmpelColor(p),
																			}"
																			>&nbsp;&nbsp;</span
																		>
																		<span class="ml-1 small">{{
																			getStatusLabel(p.status)
																		}}</span>
																	</td>
																	<td>{{ p.typ }}</td>
																	<td>{{ p.bezeichnung }}</td>
																	<td>
																		{{ p.faelligkeit | moment('DD.MM.YYYY') }}
																	</td>
																	<td>
																		{{
																			p.geplanter_termin
																				? $options.filters.moment(
																						p.geplanter_termin,
																						'DD.MM.YYYY',
																					)
																				: '—'
																		}}
																	</td>
																	<td>
																		{{
																			p.letztes_datum
																				? $options.filters.moment(
																						p.letztes_datum,
																						'DD.MM.YYYY',
																					)
																				: '—'
																		}}
																	</td>
																	<td v-if="canDmEdit">
																		<button
																			class="btn btn-outline-primary btn-sm btn-icon"
																			@click="openPruefungModal(p)"
																			:title="$t('dm_common_btn_edit')"
																		>
																			<em class="icon ni ni-edit"></em>
																		</button>
																	</td>
																</tr>
															</tbody>
														</table>
													</div>
												</div>
											</div>

											<!-- Inspect Prüfungen -->
											<div
												class="card card-bordered mt-3"
												v-if="
													!isNew &&
														anlage.pruefungen &&
														anlage.pruefungen.length > 0
												"
											>
												<div class="card-inner">
													<h5 class="card-title">
														{{ $t('dm_anlage_section_inspect_reports') }}
													</h5>
													<div class="table-responsive">
														<table class="table table-striped table-sm">
															<thead>
																<tr>
																	<th>{{ $t('dm_anlage_col_project') }}</th>
																	<th>{{ $t('dm_anlage_col_inspection') }}</th>
																	<th>
																		{{ $t('dm_anlage_col_last_updated') }}
																	</th>
																</tr>
															</thead>
															<tbody>
																<tr v-for="p in anlage.pruefungen" :key="p._id">
																	<td>{{ p.project_id }}</td>
																	<td>{{ p.inspect_id }}</td>
																	<td>
																		{{ p.updated_at | moment('DD.MM.YYYY') }}
																	</td>
																</tr>
															</tbody>
														</table>
													</div>
												</div>
											</div>

											<!-- Action buttons -->
											<div class="card card-bordered mt-3" v-if="canDmEdit">
												<div class="card-inner">
													<div
														class="d-flex align-items-center justify-content-between"
													>
														<div>
															<button
																class="btn btn-lg btn-primary"
																@click="saveAnlage"
																:disabled="saving"
															>
																{{
																	isNew
																		? $t('dm_anlage_btn_create')
																		: $t('dm_common_btn_save')
																}}
															</button>
															<button
																v-if="
																	!isNew &&
																		canDmManage &&
																		!(
																			anlage.status_8p2 &&
																			anlage.status_8p2.bestaetigt
																		)
																"
																class="btn btn-outline-success ml-2"
																@click="confirmAnlageAction"
															>
																<em class="icon ni ni-check"></em>
																<span>{{ $t('dm_common_btn_confirm') }}</span>
															</button>
														</div>
														<button
															v-if="!isNew && canDmManage"
															class="btn btn-sm btn-outline-danger"
															@click="deleteAnlageAction"
														>
															<em class="icon ni ni-trash"></em>
															<span>{{ $t('dm_common_btn_delete') }}</span>
														</button>
													</div>
												</div>
											</div>
										</div>
									</div>
									<!-- Modal Prüfung -->
									<div class="modal fade" tabindex="-1" id="modalPruefung">
										<div class="modal-dialog" role="document">
											<div class="modal-content">
												<a
													href="#"
													class="close"
													data-dismiss="modal"
													aria-label="Close"
												>
													<em class="icon ni ni-cross"></em>
												</a>
												<div class="modal-header">
													<h5 class="modal-title">
														{{
															pruefungEntry._id
																? $t('dm_anlage_modal_edit_inspection')
																: $t('dm_anlage_modal_new_inspection')
														}}
													</h5>
												</div>
												<div class="modal-body">
													<div class="alert alert-danger" v-if="pruefungError">
														{{ pruefungError }}
													</div>

													<h6 class="overline-title overline-title-alt mb-3">
														{{ $t('dm_anlage_section_basic') }}
													</h6>
													<div class="row gy-4">
														<div class="col-sm-6">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_anlage_label_inspection_type')
																}}</label>
																<select
																	class="form-control"
																	v-model="pruefungEntry.typ"
																	@change="onPruefungTypChange"
																>
																	<option value="">{{
																		$t('dm_common_please_select')
																	}}</option>
																	<option
																		v-for="pt in filteredPruefungstypen"
																		:key="pt.value"
																		:value="pt.value"
																		>{{ pt.label }}</option
																	>
																</select>
															</div>
														</div>
														<div class="col-sm-6">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_common_col_designation')
																}}</label>
																<input
																	type="text"
																	class="form-control"
																	v-model="pruefungEntry.bezeichnung"
																/>
															</div>
														</div>
													</div>
													<div class="row gy-4 mt-2">
														<div class="col-sm-6">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_common_col_due_date')
																}}</label>
																<input
																	type="date"
																	class="form-control"
																	v-model="pruefungEntry.faelligkeit"
																/>
															</div>
														</div>
														<div class="col-sm-6">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_common_col_status')
																}}</label>
																<select
																	class="form-control"
																	v-model="pruefungEntry.status"
																>
																	<option value="offen">{{
																		$t('dm_anlage_status_open')
																	}}</option>
																	<option value="geplant">{{
																		$t('dm_anlage_status_planned')
																	}}</option>
																	<option value="durchgefuehrt">{{
																		$t('dm_anlage_status_completed')
																	}}</option>
																</select>
															</div>
														</div>
													</div>

													<hr class="preview-hr" />
													<h6 class="overline-title overline-title-alt mb-3">
														{{ $t('dm_anlage_section_scheduling') }}
													</h6>
													<div class="row gy-4">
														<div class="col-sm-4">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_anlage_label_planned_date')
																}}</label>
																<input
																	type="date"
																	class="form-control"
																	v-model="pruefungEntry.geplanter_termin"
																/>
															</div>
														</div>
														<div class="col-sm-4">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_anlage_label_last_date')
																}}</label>
																<input
																	type="date"
																	class="form-control"
																	v-model="pruefungEntry.letztes_datum"
																/>
															</div>
														</div>
														<div class="col-sm-4">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_anlage_label_responsible_inspector')
																}}</label>
																<input
																	type="text"
																	class="form-control"
																	v-model="
																		pruefungEntry.zustaendiger_pruefer_id
																	"
																/>
															</div>
														</div>
													</div>

													<hr class="preview-hr" />
													<h6 class="overline-title overline-title-alt mb-3">
														{{ $t('dm_anlage_section_report') }}
													</h6>
													<div class="row gy-4">
														<div class="col-sm-6">
															<div class="form-group">
																<label class="form-label">{{
																	$t('dm_anlage_label_report_sent_date')
																}}</label>
																<input
																	type="date"
																	class="form-control"
																	v-model="pruefungEntry.bericht_versendet_am"
																/>
															</div>
														</div>
														<div class="col-sm-6 d-flex align-items-end">
															<div class="custom-control custom-checkbox">
																<input
																	type="checkbox"
																	class="custom-control-input"
																	id="berichtHersteller"
																	v-model="
																		pruefungEntry.bericht_versendet_an_hersteller
																	"
																/>
																<label
																	class="custom-control-label"
																	for="berichtHersteller"
																	>{{
																		$t('dm_anlage_label_sent_to_manufacturer')
																	}}</label
																>
															</div>
														</div>
													</div>

													<!-- BPW-spezifische Felder -->
													<div v-if="pruefungEntry.typ === 'BPW'">
														<hr class="preview-hr" />
														<h6 class="overline-title overline-title-alt mb-3">
															{{ $t('dm_anlage_section_bpw_data') }}
														</h6>
														<div class="row gy-4">
															<div class="col-sm-6">
																<div class="form-group">
																	<label class="form-label">{{
																		$t('dm_anlage_label_bpw_proven_until')
																	}}</label>
																	<input
																		type="date"
																		class="form-control"
																		v-model="pruefungEntry.bpw_nachgewiesen_bis"
																	/>
																</div>
															</div>
															<div class="col-sm-6">
																<div class="form-group">
																	<label class="form-label">{{
																		$t('dm_anlage_label_defined_weaknesses')
																	}}</label>
																	<input
																		type="text"
																		class="form-control"
																		v-model="
																			pruefungEntry.definierte_schwachstellen
																		"
																	/>
																</div>
															</div>
														</div>
													</div>

												</div>
												<div class="modal-footer bg-light d-flex justify-content-end">
													<button type="button" class="btn btn-sm btn-secondary mr-2" data-dismiss="modal">{{ $t('dm_common_btn_close') }}</button>
													<button type="button" class="btn btn-sm btn-primary" @click="savePruefung">
														{{ pruefungEntry._id ? $t('dm_common_btn_update') : $t('dm_common_btn_add') }}
													</button>
												</div>
											</div>
										</div>
									</div>

									<Footer />
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import Header from '../../../components/Header.vue';
import Navigation from '../../../components/Navigation.vue';
import Footer from '../../../components/Footer.vue';
import dmPermissionsMixin from '@/mixins/dm-permissions-mixin';
import dmTableMixin from '@/mixins/dm-table-mixin';
import {
	getAnlage,
	createAnlage,
	updateAnlage,
	deleteAnlage,
	confirmAnlage,
	getParksList,
	getAdressenList,
	getPruefungsfristen,
	addPruefung,
	updatePruefung,
} from '@/services/dm.service';

function emptyAnlage() {
	return {
		seriennummer: '',
		typ: '',
		hersteller_kuerzel: '',
		contractors: [],
		anlagendaten: {
			hersteller: '',
			typ_bezeichnung: '',
			leistung_kw: null,
			ib_datum: '',
			anlagenzugang: '',
			besonderheiten: '',
		},
		vertragsinfo: {
			epk: {
				status: '',
				epk_ende_datum: '',
			},
		},
		projekt: {
			projekt_alt: '',
			projekt_neu: '',
			zu_klaeren: '',
		},
		standort: {
			name: '',
			park_id: '',
			standortnr_hersteller: '',
			standortnr_baugenehmigung: '',
			standortnr_projektierer: '',
			adresse: {
				strasse: '',
				plz: '',
				ort: '',
				bundesland: '',
				land: '',
			},
			geokoordinaten: {
				type: 'Point',
				coordinates: [0, 0],
			},
		},
		anschriften: {
			betreiber_id: '',
			auftragnehmer_id: '',
			betriebsfuehrer_id: '',
			rechnungsempfaenger_id: '',
			genehmigungsbehoerde_id: '',
			anlagen_betreuer_vor_ort: {
				name: '',
				telefon: '',
				email: '',
			},
		},
		wea_daten: {
			rotordurchmesser_m: null,
			nabenhoehe_m: null,
			technologie: '',
		},
		pv_daten: {
			leistung_kwp: null,
			modulanzahl: null,
			modultyp: '',
			modulhersteller: '',
			wechselrichter_typ: '',
			aufstellungswinkel_grad: null,
			ausrichtung_grad: null,
			flaeche_m2: null,
			tracking: false,
		},
		bat_daten: {
			kapazitaet_kwh: null,
			leistung_kw_entladung: null,
			leistung_kw_ladung: null,
			technologie: '',
			anzahl_module: null,
			betriebstemperatur_min_c: null,
			betriebstemperatur_max_c: null,
			zyklen_garantiert: null,
		},
		betriebseinschraenkungen: [],
		status_8p2: {
			bestaetigt: false,
		},
	};
}

function ensureNestedStructure(anlage) {
	var empty = emptyAnlage();
	if (!anlage.anlagendaten) anlage.anlagendaten = empty.anlagendaten;
	if (!anlage.vertragsinfo) anlage.vertragsinfo = empty.vertragsinfo;
	if (!anlage.vertragsinfo.epk)
		anlage.vertragsinfo.epk = empty.vertragsinfo.epk;
	if (!anlage.projekt) anlage.projekt = empty.projekt;
	if (!anlage.standort) anlage.standort = empty.standort;
	if (!anlage.standort.adresse)
		anlage.standort.adresse = empty.standort.adresse;
	if (!anlage.standort.geokoordinaten)
		anlage.standort.geokoordinaten = empty.standort.geokoordinaten;
	if (!anlage.standort.geokoordinaten.coordinates)
		anlage.standort.geokoordinaten.coordinates = [0, 0];
	if (!anlage.anschriften) anlage.anschriften = empty.anschriften;
	if (!anlage.anschriften.auftragnehmer_id)
		anlage.anschriften.auftragnehmer_id = '';
	if (!anlage.anschriften.anlagen_betreuer_vor_ort)
		anlage.anschriften.anlagen_betreuer_vor_ort =
			empty.anschriften.anlagen_betreuer_vor_ort;
	if (!anlage.wea_daten) anlage.wea_daten = empty.wea_daten;
	if (!anlage.pv_daten) anlage.pv_daten = empty.pv_daten;
	if (!anlage.bat_daten) anlage.bat_daten = empty.bat_daten;
	if (!anlage.status_8p2) anlage.status_8p2 = empty.status_8p2;
	if (!Array.isArray(anlage.contractors)) anlage.contractors = [];
	if (!Array.isArray(anlage.betriebseinschraenkungen))
		anlage.betriebseinschraenkungen = [];
	// Convert ISO date strings to YYYY-MM-DD for <input type="date">
	if (
		anlage.anlagendaten.ib_datum &&
		anlage.anlagendaten.ib_datum.length > 10
	) {
		anlage.anlagendaten.ib_datum = anlage.anlagendaten.ib_datum.substring(
			0,
			10,
		);
	}
	if (
		anlage.vertragsinfo.epk.epk_ende_datum &&
		anlage.vertragsinfo.epk.epk_ende_datum.length > 10
	) {
		anlage.vertragsinfo.epk.epk_ende_datum = anlage.vertragsinfo.epk.epk_ende_datum.substring(
			0,
			10,
		);
	}
	return anlage;
}

export default {
	name: 'DmAnlageDetail',
	components: { Header, Navigation, Footer },
	mixins: [dmPermissionsMixin, dmTableMixin],
	data() {
		return {
			fullLoader: null,
			anlage: emptyAnlage(),
			parks: [],
			adressen: [],
			pruefungen: [],
			pruefungEntry: {},
			pruefungError: '',
			errorMessage: '',
			saving: false,
		};
	},
	computed: {
		allePruefungstypen() {
			return [
				{
					value: 'WKP_M',
					label: this.$t('dm_anlage_inspection_type_wkp_m'),
					anlagen: ['WEA'],
				},
				{
					value: 'WKP_RB',
					label: this.$t('dm_anlage_inspection_type_wkp_rb'),
					anlagen: ['WEA'],
				},
				{
					value: 'BPW',
					label: this.$t('dm_anlage_inspection_type_bpw'),
					anlagen: ['WEA', 'PV', 'BAT'],
				},
				{
					value: 'Druckbehaelter',
					label: this.$t('dm_anlage_inspection_type_druckbehaelter'),
					anlagen: ['WEA'],
				},
				{
					value: 'Lift_Leiter',
					label: this.$t('dm_anlage_inspection_type_lift_leiter'),
					anlagen: ['WEA'],
				},
				{
					value: 'AwSV',
					label: this.$t('dm_anlage_inspection_type_awsv'),
					anlagen: ['WEA', 'BAT'],
				},
				{
					value: 'Blitzschutz',
					label: this.$t('dm_anlage_inspection_type_blitzschutz'),
					anlagen: ['WEA', 'PV', 'BAT'],
				},
				{
					value: 'Elektrische_Anlage',
					label: this.$t('dm_anlage_inspection_type_elektrische'),
					anlagen: ['PV', 'BAT'],
				},
				{
					value: 'Wechselrichter_Wartung',
					label: this.$t('dm_anlage_inspection_type_wechselrichter'),
					anlagen: ['PV'],
				},
				{
					value: 'Thermografie',
					label: this.$t('dm_anlage_inspection_type_thermografie'),
					anlagen: ['PV'],
				},
				{
					value: 'Brandschutz',
					label: this.$t('dm_anlage_inspection_type_brandschutz'),
					anlagen: ['BAT'],
				},
				{
					value: 'BMS_Check',
					label: this.$t('dm_anlage_inspection_type_bms'),
					anlagen: ['BAT'],
				},
				{
					value: 'Kapazitaetstest',
					label: this.$t('dm_anlage_inspection_type_kapazitaetstest'),
					anlagen: ['BAT'],
				},
			];
		},
		loading_page() {
			return (
				this.$store.state.loading_page &&
				this.$store.state.loading_page == 'setup'
			);
		},
		isNew() {
			return this.$route.params.id === 'new';
		},
		latitude: {
			get() {
				return this.anlage.standort.geokoordinaten.coordinates[1] || 0;
			},
			set(val) {
				this.$set(this.anlage.standort.geokoordinaten.coordinates, 1, val);
			},
		},
		longitude: {
			get() {
				return this.anlage.standort.geokoordinaten.coordinates[0] || 0;
			},
			set(val) {
				this.$set(this.anlage.standort.geokoordinaten.coordinates, 0, val);
			},
		},
		filteredPruefungstypen() {
			var typ = this.anlage.typ;
			if (!typ) return this.allePruefungstypen;
			return this.allePruefungstypen.filter(function(pt) {
				return pt.anlagen.indexOf(typ) !== -1;
			});
		},
	},
	created() {
		this.loadSupporting();
		if (!this.isNew) {
			this.loadAnlage();
			this.loadPruefungsfristen();
		}
	},
	methods: {
		loadSupporting() {
			getParksList()
				.then((d) => {
					if (d.data && d.data.list) this.parks = d.data.list;
				})
				.catch(() => {});
			getAdressenList()
				.then((d) => {
					if (d.data && d.data.list) this.adressen = d.data.list;
				})
				.catch(() => {});
		},
		loadAnlage() {
			this.showLoader();
			getAnlage(this.$route.params.id)
				.then((d) => {
					this.hideLoader();
					if (d.data && d.data.anlage) {
						this.anlage = ensureNestedStructure(d.data.anlage);
					}
				})
				.catch((e) => {
					this.hideLoader();
					this.$store.dispatch('api_error', e);
				});
		},
		saveAnlage() {
			this.errorMessage = '';
			this.saving = true;
			if (this.isNew) {
				createAnlage(this.anlage)
					.then((d) => {
						this.saving = false;
						var err = this.getApiError(d);
						if (err) {
							this.errorMessage = err;
						} else if (d.data && d.data._id) {
							this.$router.push('/dm/anlage/' + d.data._id);
						}
					})
					.catch(() => {
						this.saving = false;
						this.errorMessage = this.$t('dm_common_error');
					});
			} else {
				updateAnlage(this.anlage._id, this.anlage.version, this.anlage)
					.then((d) => {
						this.saving = false;
						var err = this.getApiError(d);
						if (err) {
							if (err === 'Versionskonflikt') {
								this.$swal({
									title: this.$t('dm_anlage_version_conflict_title'),
									text: this.$t('dm_anlage_version_conflict_text'),
									icon: 'warning',
									confirmButtonText: 'OK',
								}).then(() => {
									this.loadAnlage();
								});
							} else {
								this.errorMessage = err;
							}
						} else if (d.data && d.data.anlage) {
							this.anlage = ensureNestedStructure(d.data.anlage);
							this.$swal({
								title: this.$t('dm_common_saved'),
								icon: 'success',
								timer: 1500,
								showConfirmButton: false,
							});
						}
					})
					.catch(() => {
						this.saving = false;
						this.errorMessage = this.$t('dm_common_error');
					});
			}
		},
		confirmAnlageAction() {
			this.$swal({
				title: this.$t('dm_anlage_confirm_confirm_title'),
				text: this.$t('dm_common_cannot_be_undone'),
				icon: 'warning',
				showCancelButton: true,
				confirmButtonText: this.$t('dm_common_btn_confirm_yes'),
			}).then((result) => {
				if (result.isConfirmed) {
					confirmAnlage(this.anlage._id).then((d) => {
						if (d.data && d.data.anlage) {
							this.anlage = ensureNestedStructure(d.data.anlage);
						}
					});
				}
			});
		},
		deleteAnlageAction() {
			this.$swal({
				title: this.$t('dm_anlage_confirm_delete_title'),
				icon: 'warning',
				showCancelButton: true,
				confirmButtonColor: '#d33',
				confirmButtonText: this.$t('dm_common_btn_delete_yes'),
			}).then((result) => {
				if (result.isConfirmed) {
					deleteAnlage(this.anlage._id).then(() => {
						this.$router.push('/dm/anlagen');
					});
				}
			});
		},
		addBetriebseinschraenkung() {
			this.anlage.betriebseinschraenkungen.push({ typ: '', beschreibung: '' });
		},
		showLoader() {
			this.fullLoader = this.$loading.show({
				container: null,
				canCancel: false,
				height: 40,
				width: 40,
				loader: 'spinner',
				color: '#E8751A',
				backgroundColor: '#EEF2FE',
				opacity: 0.6,
			});
		},
		hideLoader() {
			if (this.fullLoader) this.fullLoader.hide();
		},
		// Prüfungsfristen
		loadPruefungsfristen() {
			getPruefungsfristen(this.$route.params.id)
				.then((d) => {
					if (d.data && d.data.fristen && d.data.fristen.pruefungen) {
						this.pruefungen = d.data.fristen.pruefungen;
					}
				})
				.catch(() => {});
		},
		openPruefungModal(pruefung) {
			this.pruefungError = '';
			if (pruefung) {
				this.pruefungEntry = JSON.parse(JSON.stringify(pruefung));
				if (
					this.pruefungEntry.faelligkeit &&
					this.pruefungEntry.faelligkeit.length > 10
				)
					this.pruefungEntry.faelligkeit = this.pruefungEntry.faelligkeit.substring(
						0,
						10,
					);
				if (
					this.pruefungEntry.geplanter_termin &&
					this.pruefungEntry.geplanter_termin.length > 10
				)
					this.pruefungEntry.geplanter_termin = this.pruefungEntry.geplanter_termin.substring(
						0,
						10,
					);
				if (
					this.pruefungEntry.letztes_datum &&
					this.pruefungEntry.letztes_datum.length > 10
				)
					this.pruefungEntry.letztes_datum = this.pruefungEntry.letztes_datum.substring(
						0,
						10,
					);
				if (
					this.pruefungEntry.bericht_versendet_am &&
					this.pruefungEntry.bericht_versendet_am.length > 10
				)
					this.pruefungEntry.bericht_versendet_am = this.pruefungEntry.bericht_versendet_am.substring(
						0,
						10,
					);
				if (
					this.pruefungEntry.bpw_nachgewiesen_bis &&
					this.pruefungEntry.bpw_nachgewiesen_bis.length > 10
				)
					this.pruefungEntry.bpw_nachgewiesen_bis = this.pruefungEntry.bpw_nachgewiesen_bis.substring(
						0,
						10,
					);
			} else {
				this.pruefungEntry = {
					typ: '',
					bezeichnung: '',
					faelligkeit: '',
					status: 'offen',
					geplanter_termin: '',
					letztes_datum: '',
					bericht_versendet_am: '',
					bericht_versendet_an_hersteller: false,
					zustaendiger_pruefer_id: '',
					bpw_nachgewiesen_bis: '',
					definierte_schwachstellen: '',
				};
			}
			$('#modalPruefung').modal('show');
		},
		onPruefungTypChange() {
			var typ = this.pruefungEntry.typ;
			var found = this.allePruefungstypen.find(function(pt) {
				return pt.value === typ;
			});
			if (found && !this.pruefungEntry.bezeichnung) {
				this.pruefungEntry.bezeichnung = found.label;
			}
		},
		savePruefung() {
			this.pruefungError = '';
			if (!this.pruefungEntry.typ) {
				this.pruefungError = this.$t(
					'dm_anlage_error_inspection_type_required',
				);
				return;
			}
			if (!this.pruefungEntry.faelligkeit) {
				this.pruefungError = this.$t('dm_anlage_error_due_date_required');
				return;
			}
			var anlageId = this.$route.params.id;
			if (this.pruefungEntry._id) {
				updatePruefung(anlageId, this.pruefungEntry._id, this.pruefungEntry)
					.then((d) => {
						var err = this.getApiError(d);
						if (err) {
							this.pruefungError = err;
						} else {
							$('#modalPruefung').modal('hide');
							this.loadPruefungsfristen();
						}
					})
					.catch(() => {
						this.pruefungError = this.$t('dm_common_error');
					});
			} else {
				addPruefung(anlageId, this.pruefungEntry)
					.then((d) => {
						var err = this.getApiError(d);
						if (err) {
							this.pruefungError = err;
						} else {
							$('#modalPruefung').modal('hide');
							this.loadPruefungsfristen();
						}
					})
					.catch(() => {
						this.pruefungError = this.$t('dm_common_error');
					});
			}
		},
		getAmpelColor(pruefung) {
			if (pruefung.status === 'durchgefuehrt') return '#24c45f';
			if (!pruefung.faelligkeit) return '#999';
			var now = new Date();
			var faellig = new Date(pruefung.faelligkeit);
			var diffDays = Math.ceil((faellig - now) / (1000 * 60 * 60 * 24));
			if (diffDays < 0) return '#DC2626';
			if (diffDays < 90) return '#EF4444';
			if (diffDays < 180) return '#F97316';
			if (diffDays < 270) return '#EAB308';
			return '#24c45f';
		},
		getStatusLabel(status) {
			var labels = {
				offen: this.$t('dm_anlage_status_open'),
				geplant: this.$t('dm_anlage_status_planned'),
				durchgefuehrt: this.$t('dm_anlage_status_completed'),
			};
			return labels[status] || status;
		},
	},
};
</script>

<style scoped></style>
